import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Gauge,
  Loader,
  RefreshCw,
  Search,
  Server,
  Users,
} from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { useDebounce } from '../hooks/useDebounce';
import { throttleApiService } from '../services/throttleApi';
import type {
  AntiLagData,
  CacheEnvelope,
  ThrottleHealth,
  ThrottleServerIndex,
} from '../types/throttle';
import { ThrottleHealthBadge } from '../components/throttle/ThrottleHealthBadge';
import { ThrottleServerTable, ThrottleServerRow } from '../components/throttle/ThrottleServerTable';
import { ThrottleConfigPanel } from '../components/throttle/ThrottleConfigPanel';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { MSPT_TEXT_CLASSES, formatEpochMsAgo, getMsptSeverity } from '../utils/throttle';
import { cn } from '../utils/cn';

/**
 * The backend cache refreshes at most once a minute; the guidance is to poll
 * every 60–90 s with jitter so open tabs don't sync up.
 */
const POLL_MS = 60_000 + Math.round(Math.random() * 20_000);

type SortKey = 'mspt' | 'tps' | 'flagged' | 'name';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'mspt', label: 'Most loaded (MSPT)' },
  { value: 'tps', label: 'Lowest TPS' },
  { value: 'flagged', label: 'Most flagged chunks' },
  { value: 'name', label: 'Name (A–Z)' },
];

/** Spinner rendered through EmptyState's icon slot (cold-start state). */
const SpinningLoader = ({ className }: { className?: string }) => (
  <Loader className={cn(className, 'animate-spin')} />
);

const PAGE_DESCRIPTION =
  'Anti-lag chunk throttling across the network. Data is cached and refreshed about once a minute.';

export const ChunkThrottleView = () => {
  useDocumentTitle('Chunk Throttle');

  const [health, setHealth] = useState<ThrottleHealth | null>(null);
  const [index, setIndex] = useState<CacheEnvelope<ThrottleServerIndex> | null>(null);
  const [config, setConfig] = useState<CacheEnvelope<AntiLagData> | null>(null);
  const [isColdStart, setIsColdStart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [sortKey, setSortKey] = useState<SortKey>('mspt');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [groupByCluster, setGroupByCluster] = useState(true);

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    // Requesting /health during a cold start also kicks off the API's bootstrap poll.
    const [healthResult, serversResult] = await Promise.allSettled([
      throttleApiService.getHealth(),
      throttleApiService.getServers(),
    ]);

    if (healthResult.status === 'fulfilled') {
      setHealth(healthResult.value);
    }

    if (serversResult.status === 'fulfilled') {
      if (serversResult.value === null) {
        // Cold start — cache not populated yet. Keep any previous snapshot.
        setIsColdStart(true);
        setError(null);
      } else {
        setIndex(serversResult.value);
        setIsColdStart(false);
        setError(null);
      }
    } else {
      // Keep the last good snapshot and surface a soft failure.
      setError(
        serversResult.reason instanceof Error
          ? serversResult.reason.message
          : 'Failed to load server data',
      );
    }

    setIsLoading(false);
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    void refresh();
    // Config only changes when staff edit it — one session fetch is enough
    // (the service caches it for 10 minutes anyway).
    throttleApiService
      .getConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [refresh]);

  useVisiblePolling(() => void refresh(), POLL_MS);

  const allRows = useMemo<ThrottleServerRow[]>(() => {
    if (!index) return [];
    return Object.entries(index.data).map(([fqdn, summary]) => ({ fqdn, summary }));
  }, [index]);

  const visibleRows = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    const filtered = allRows.filter(({ fqdn, summary }) => {
      if (onlyFlagged && summary.flaggedChunkCount === 0) return false;
      if (!query) return true;
      return (
        fqdn.toLowerCase().includes(query) ||
        (summary.clusterId?.toLowerCase().includes(query) ?? false) ||
        (summary.serverId?.toLowerCase().includes(query) ?? false)
      );
    });

    // Rows with no reading sort last regardless of direction.
    const sorted = [...filtered];
    switch (sortKey) {
      case 'mspt':
        sorted.sort((a, b) => (b.summary.mspt ?? -1) - (a.summary.mspt ?? -1));
        break;
      case 'tps':
        sorted.sort((a, b) => (a.summary.tps ?? 21) - (b.summary.tps ?? 21));
        break;
      case 'flagged':
        sorted.sort(
          (a, b) =>
            b.summary.flaggedChunkCount - a.summary.flaggedChunkCount ||
            (b.summary.mspt ?? -1) - (a.summary.mspt ?? -1),
        );
        break;
      case 'name':
        sorted.sort((a, b) => a.fqdn.localeCompare(b.fqdn));
        break;
    }
    return sorted;
  }, [allRows, debouncedSearch, onlyFlagged, sortKey]);

  const stats = useMemo(() => {
    const online = allRows.filter((row) => row.summary.online);
    const flaggedServers = allRows.filter((row) => row.summary.flaggedChunkCount > 0);
    const totalFlaggedChunks = allRows.reduce(
      (sum, row) => sum + row.summary.flaggedChunkCount,
      0,
    );
    const totalPlayers = online.reduce((sum, row) => sum + (row.summary.playerCount ?? 0), 0);
    let worst: ThrottleServerRow | null = null;
    for (const row of online) {
      if (row.summary.mspt == null) continue;
      if (!worst || row.summary.mspt > (worst.summary.mspt ?? 0)) worst = row;
    }
    return {
      total: allRows.length,
      onlineCount: online.length,
      totalPlayers,
      flaggedServerCount: flaggedServers.length,
      totalFlaggedChunks,
      worst,
    };
  }, [allRows]);

  const pipelineDegraded = health != null && (health.state === 'stale' || health.state === 'failing');

  // ── First load ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Infrastructure"
          title="Chunk Throttle"
          description="Loading anti-lag data..."
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <StatTile key={i} label="" value="" loading />
          ))}
        </div>
        <div className="glass-card p-4 md:p-5" aria-busy="true">
          <SkeletonLoader className="h-4 w-1/3 mb-4" />
          <SkeletonLoader className="h-4 w-full mb-2" />
          <SkeletonLoader className="h-4 w-full mb-2" />
          <SkeletonLoader className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  // ── Hard error with no snapshot at all ─────────────────────────────────
  if (error && !index && !isColdStart) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Infrastructure"
          title="Chunk Throttle"
          titleAdornment={<ThrottleHealthBadge health={health} />}
          description={PAGE_DESCRIPTION}
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to Load Throttle Data"
          description={error}
          action={
            <button onClick={() => void refresh()} className="glass-button">
              Retry
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Infrastructure"
        title="Chunk Throttle"
        titleAdornment={<ThrottleHealthBadge health={health} />}
        description={PAGE_DESCRIPTION}
        meta={
          index ? (
            <span className="tabular-nums" title={new Date(index.fetchedAt).toLocaleString()}>
              as of {formatEpochMsAgo(index.fetchedAt)}
            </span>
          ) : undefined
        }
        actions={
          <button
            onClick={() => void refresh()}
            className="glass-button touch-manipulation"
            disabled={isRefreshing}
            title="Refresh now"
          >
            <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
            Refresh
          </button>
        }
      />

      {/* Degraded-pipeline banner: other routes keep serving cached data, so keep rendering. */}
      {pipelineDegraded && (
        <div className="alert alert-warning">
          <AlertTriangle />
          <p>
            The throttle data pipeline is {health?.state === 'failing' ? 'failing' : 'stale'} — the
            figures below may be outdated.
            {health?.lastSuccessAt != null &&
              ` Last successful poll ${formatEpochMsAgo(health.lastSuccessAt)}.`}
          </p>
        </div>
      )}

      {/* Refresh failed but we still have a snapshot. */}
      {error && index && (
        <div className="alert alert-warning">
          <AlertTriangle />
          <p>
            Refresh failed — showing data from {formatEpochMsAgo(index.fetchedAt)}.
          </p>
        </div>
      )}

      {/* Cold start: the /health request above triggers the bootstrap poll; polling retries. */}
      {isColdStart && !index && (
        <EmptyState
          tone="accent"
          icon={SpinningLoader}
          title="Cache is warming up"
          description="The throttle service has not completed its first poll yet. This page retries automatically — data usually appears within a minute."
        />
      )}

      {index && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="Servers online"
              icon={Server}
              tone="accent"
              value={stats.onlineCount}
              hint={`of ${stats.total} server${stats.total === 1 ? '' : 's'}`}
            />
            <StatTile
              label="Players online"
              icon={Users}
              tone="info"
              value={stats.totalPlayers}
            />
            <StatTile
              label="Flagged chunks"
              icon={AlertTriangle}
              tone={stats.totalFlaggedChunks > 0 ? 'amber' : 'neutral'}
              value={
                <span className={cn(stats.totalFlaggedChunks > 0 && 'text-amber-300')}>
                  {stats.totalFlaggedChunks}
                </span>
              }
              hint={`on ${stats.flaggedServerCount} server${stats.flaggedServerCount === 1 ? '' : 's'}`}
              title="Capped at the 10 worst chunks per dimension — real counts may be higher"
            />
            {stats.worst && stats.worst.summary.mspt != null ? (
              <StatTile
                label="Worst MSPT (50 ms budget)"
                icon={Gauge}
                tone="purple"
                value={
                  <span className={MSPT_TEXT_CLASSES[getMsptSeverity(stats.worst.summary.mspt)]}>
                    {stats.worst.summary.mspt.toFixed(1)}
                  </span>
                }
                hint={<span className="font-mono">{stats.worst.fqdn}</span>}
              />
            ) : (
              <StatTile
                label="Worst MSPT (50 ms budget)"
                icon={Gauge}
                tone="purple"
                value={<span className="text-glass-500">—</span>}
              />
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                className="glass-input w-full pl-9"
                placeholder="Search by FQDN, cluster, or server id..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                className="glass-select"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                title="Sort order"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer touch-manipulation">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={onlyFlagged}
                  onChange={(e) => setOnlyFlagged(e.target.checked)}
                />
                <span>Only flagged</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer touch-manipulation">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={groupByCluster}
                  onChange={(e) => setGroupByCluster(e.target.checked)}
                />
                <span>Group by cluster</span>
              </label>
              <span className="text-xs text-glass-400 tabular-nums md:ml-auto">
                {visibleRows.length} of {allRows.length} server{allRows.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>

          {/* Fleet table */}
          <ThrottleServerTable rows={visibleRows} groupByCluster={groupByCluster} />

          {/* Network anti-lag config */}
          <ThrottleConfigPanel config={config} />
        </>
      )}
    </div>
  );
};
